import { z } from 'zod';
import {
  PropertySharingDestination,
  PropertySharingField,
  PropertySharingTextPart,
  PropertySharingFieldPart,
  PropertySharingIdentifierPart,
  PropertySharingIdentifierSelector,
  PropertySharingAnyNamespace,
  PropertySharingExactNamespace,
} from '../../../api/generated';

const parameterSchemas = [
  z.object({
    kind: z.literal(PropertySharingFieldPart.kind.PROPERTY),
    field: z.nativeEnum(PropertySharingField),
  }),
  z.object({
    kind: z.literal(PropertySharingIdentifierPart.kind.IDENTIFIER),
    field: z.nativeEnum(PropertySharingIdentifierPart.field),
    selector: z.object({
      system: z.string().trim().min(1),
      kind: z.nativeEnum(PropertySharingIdentifierSelector.kind),
      label: z.string().nullable(),
      namespace: z.discriminatedUnion('mode', [
        z.object({ mode: z.literal(PropertySharingAnyNamespace.mode.ANY) }),
        z.object({
          mode: z.literal(PropertySharingExactNamespace.mode.EXACT),
          value: z.string().trim().min(1).nullable(),
        }),
      ]),
    }),
  }),
] as const;

const parameterSchema = z.discriminatedUnion('kind', parameterSchemas);
const partSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(PropertySharingTextPart.kind.TEXT),
    value: z.string(),
  }),
  ...parameterSchemas,
]);

/**
 * Parses the generated policy shape and validates editable form inputs.
 * Defaults and authoritative template semantics remain backend-owned; this
 * schema supplies immediate editor feedback, not destination resolution.
 */
export const propertyLinksFormSchema = z.object({
  version: z.literal(1),
  priority: z
    .array(z.nativeEnum(PropertySharingDestination))
    .length(3)
    .refine((priority) => new Set(priority).size === 3),
  externalSite: z
    .object({
      origin: z
        .string()
        .url()
        .refine((value) => {
          try {
            const url = new URL(value);
            return (
              ['http:', 'https:'].includes(url.protocol) &&
              (value === url.origin || value === `${url.origin}/`)
            );
          } catch {
            return false;
          }
        }, 'Use an HTTP(S) website origin.'),
      path: z.array(partSchema).min(1).max(64),
      query: z
        .array(
          z
            .object({
              key: z.string(),
              value: z.array(partSchema).min(1).max(32),
              encoded: z.boolean().optional(),
              omitEquals: z.boolean().optional(),
            })
            .refine((entry) => {
              const bare =
                entry.encoded &&
                entry.omitEquals &&
                entry.value.every(
                  (part) =>
                    part.kind === PropertySharingTextPart.kind.TEXT &&
                    part.value === ''
                );
              const invalidEncodedText =
                entry.encoded &&
                (/[\s&#=\\]|%(?![\da-f]{2})/iu.test(entry.key) ||
                  entry.value.some(
                    (part) =>
                      part.kind === PropertySharingTextPart.kind.TEXT &&
                      /[\s&#\\]|%(?![\da-f]{2})/iu.test(part.value)
                  ));
              return (
                !invalidEncodedText &&
                (!entry.omitEquals || Boolean(bare)) &&
                (Boolean(entry.key) || Boolean(bare))
              );
            }, 'Preserve valid encoded query text; bare keys require an empty literal value.')
        )
        .max(20),
      valueMappings: z
        .array(
          z.object({
            parameter: parameterSchema,
            values: z
              .record(
                z.string().min(1).max(2048),
                z
                  .string()
                  .min(1)
                  .max(2048)
                  .refine((value) =>
                    [...value].every(
                      (char) =>
                        char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127
                    )
                  )
              )
              .refine((values) => Object.keys(values).length <= 100),
          })
        )
        .max(64)
        .optional(),
    })
    .refine(
      ({ path, query }) =>
        [...path, ...query.flatMap(({ value }) => value)].some(
          ({ kind }) => kind !== PropertySharingTextPart.kind.TEXT
        ),
      'Add at least one property or identifier parameter.'
    )
    .nullable(),
});
